import React, { useState, useRef } from "react";
import { navigate } from "@reach/router";
import Swal from "sweetalert2";
import { httpPost, httpGet } from "../../servicios";
import {
  urlRegistro,
  urlRegistroExisteNew,
  urlRegistroExisteAnai,
  verificarCedula,
  verificarRuc,
  verificarPasaporte,
  soloNumeros,
  soloAlfanumerico,
  toMayusculas,
  urlAuth,
  urlGetGrados,
  urlOpciones,
} from "../../utils";
import Usuario from "../../models/Usuarios";
import { useStore } from "../../context";
import logoanai from "../../images/logos/logoanai.png";

var moment = require("moment");

export default function RegistroNuevo() {
  const { dispatch } = useStore();
  const refCedula = useRef();
  const refRuc = useRef();
  const refPasaporte = useRef();
  const refNombres = useRef();
  const refApellidos = useRef();
  const refClave = useRef();
  const refClave2 = useRef();

  const [loading, setLoading] = useState(false);
  const [usuario, setUsuario] = useState({
    tipoDoc: "C",
    identificacion: "",
    nombres: "",
    apellidos: "",
    clave: "",
    clave2: "",
  });
  const [config, setConfig] = useState({ clave: false, clave2: false });
  const [error, setError] = useState({
    errorIden: false,
    errorNombres: false,
    errorApellidos: false,
    errorClave: false,
    errorClave2: false,
    errorPasaporte: false,
    errorRuc: false,
  });
  // Mensajes de error dinámicos
  const [errorMsg, setErrorMsg] = useState({
    identificacion: "",
    nombres: "",
    apellidos: "",
    clave: "",
    clave2: "",
  });

  // Estilo para mensajes de error
  const errorMsgStyle = { color: "#e53e3e", fontSize: "0.75rem", marginTop: 4, display: "block", fontWeight: 500 };

  const changeData = (e) => {
    const { name, value } = e.target;
    if (name === 'identificacion') {
      // Para cédula y RUC solo números, para pasaporte alfanumérico
      const filteredValue = usuario.tipoDoc === 'P' ? soloAlfanumerico(value) : soloNumeros(value);
      setUsuario({ ...usuario, [name]: filteredValue });
      // Limpiar error mientras escribe
      if (filteredValue.length > 0) {
        setErrorMsg(prev => ({ ...prev, identificacion: "" }));
        validar(idConfig[usuario.tipoDoc]?.errKey || "errorIden", false);
      }
    } else if (name === 'nombres' || name === 'apellidos') {
      // Convertir a mayúsculas automáticamente
      setUsuario({ ...usuario, [name]: toMayusculas(value) });
      // Limpiar error mientras escribe
      if (value.trim().length > 0) {
        setErrorMsg(prev => ({ ...prev, [name]: "" }));
        validar(name === 'nombres' ? "errorNombres" : "errorApellidos", false);
      }
    } else if (name === 'clave' || name === 'clave2') {
      setUsuario({ ...usuario, [name]: value });
      // Limpiar error mientras escribe
      if (value.length > 0) {
        setErrorMsg(prev => ({ ...prev, [name]: "" }));
        validar(name === 'clave' ? "errorClave" : "errorClave2", false);
      }
    } else {
      setUsuario({ ...usuario, [name]: value });
    }
  };

  const validar = (campo, value) => {
    setError((prev) => ({ ...prev, [campo]: value }));
  };

  const toggleShowPassword = (field) => {
    setConfig({ ...config, [field]: !config[field] });
  };

  /* ----- Identification label/placeholder per doc type ----- */
  const idConfig = {
    C: { label: "Número de Cédula", placeholder: "Ej: 0925521023", maxLen: 10, errKey: "errorIden", ref: refCedula },
    P: { label: "Número de Pasaporte", placeholder: "Ej: AB1234567", maxLen: 13, errKey: "errorPasaporte", ref: refPasaporte },
    R: { label: "Número de RUC", placeholder: "Ej: 0925521023001", maxLen: 13, errKey: "errorRuc", ref: refRuc },
  };
  const currentId = idConfig[usuario.tipoDoc] || idConfig["C"];

  /* ----- Validation on blur ----- */
  const blurIdentificacion = () => {
    const val = usuario.identificacion.trim();
    if (val === "") {
      validar(currentId.errKey, true);
      setErrorMsg(prev => ({ ...prev, identificacion: "Este campo es requerido" }));
      return;
    }
    if (usuario.tipoDoc === "C") {
      if (val.length !== 10) { 
        validar("errorIden", true); 
        setErrorMsg(prev => ({ ...prev, identificacion: "La cédula debe tener 10 dígitos" }));
        return; 
      }
      if (!verificarCedula(val)) { 
        validar("errorIden", true); 
        setErrorMsg(prev => ({ ...prev, identificacion: "Número de cédula inválido" }));
        return; 
      }
      validar("errorIden", false);
      setErrorMsg(prev => ({ ...prev, identificacion: "" }));
    } else if (usuario.tipoDoc === "R") {
      if (val.length !== 13) { 
        validar("errorRuc", true); 
        setErrorMsg(prev => ({ ...prev, identificacion: "El RUC debe tener 13 dígitos" }));
        return; 
      }
      if (!verificarRuc(val)) { 
        validar("errorRuc", true); 
        setErrorMsg(prev => ({ ...prev, identificacion: "RUC inválido (debe terminar en 001, 002 o 003)" }));
        return; 
      }
      validar("errorRuc", false);
      setErrorMsg(prev => ({ ...prev, identificacion: "" }));
    } else {
      if (!verificarPasaporte(val)) { 
        validar("errorPasaporte", true); 
        setErrorMsg(prev => ({ ...prev, identificacion: "Pasaporte inválido (5-13 caracteres alfanuméricos)" }));
        return; 
      }
      validar("errorPasaporte", false);
      setErrorMsg(prev => ({ ...prev, identificacion: "" }));
    }
  };

  // Validación de nombres al perder foco
  const blurNombres = () => {
    if (usuario.nombres.trim() === "") {
      validar("errorNombres", true);
      setErrorMsg(prev => ({ ...prev, nombres: "Este campo es requerido" }));
    } else {
      validar("errorNombres", false);
      setErrorMsg(prev => ({ ...prev, nombres: "" }));
    }
  };

  // Validación de apellidos al perder foco
  const blurApellidos = () => {
    if (usuario.apellidos.trim() === "") {
      validar("errorApellidos", true);
      setErrorMsg(prev => ({ ...prev, apellidos: "Este campo es requerido" }));
    } else {
      validar("errorApellidos", false);
      setErrorMsg(prev => ({ ...prev, apellidos: "" }));
    }
  };

  // Validación de clave al perder foco
  const blurClave = () => {
    if (usuario.clave.trim() === "") {
      validar("errorClave", true);
      setErrorMsg(prev => ({ ...prev, clave: "Este campo es requerido" }));
    } else if (usuario.clave.length < 4) {
      validar("errorClave", true);
      setErrorMsg(prev => ({ ...prev, clave: "La contraseña debe tener mínimo 4 caracteres" }));
    } else {
      validar("errorClave", false);
      setErrorMsg(prev => ({ ...prev, clave: "" }));
    }
  };

  // Validación de confirmación de clave al perder foco
  const blurClave2 = () => {
    if (usuario.clave2.trim() === "") {
      validar("errorClave2", true);
      setErrorMsg(prev => ({ ...prev, clave2: "Este campo es requerido" }));
    } else if (usuario.clave.trim() !== usuario.clave2.trim()) {
      validar("errorClave2", true);
      setErrorMsg(prev => ({ ...prev, clave2: "Las contraseñas no coinciden" }));
    } else {
      validar("errorClave2", false);
      setErrorMsg(prev => ({ ...prev, clave2: "" }));
    }
  };

  /* ----- Swal helper ----- */
  const showError = (msg) => {
    Swal.fire({ icon: "error", title: "Validación", text: msg, confirmButtonColor: "#0d1b3e" });
  };

  /* ----- SAVE ----- */
  const save = async () => {
    const { tipoDoc, identificacion, nombres, apellidos, clave, clave2 } = usuario;
    let _hoy = moment();
    _hoy = _hoy.format("YYYY-MM-DD HH:mm:ss");
    let hoy = new Date(_hoy);

    if (tipoDoc === "") return showError("¡Seleccione el tipo de documento!");

    if (tipoDoc === "C") {
      if (identificacion === "") { validar("errorIden", true); refCedula.current.focus(); return showError("¡Debe ingresar el número de identificación!"); }
      if (identificacion.trim().length !== 10) { validar("errorIden", true); refCedula.current.focus(); return showError("¡Número de cédula tiene que contener 10 dígitos!"); }
      if (!verificarCedula(identificacion.trim())) { validar("errorIden", true); return showError("¡Número de cédula inválido!"); }
    } else if (tipoDoc === "R") {
      if (identificacion === "") { validar("errorRuc", true); refRuc.current.focus(); return showError("¡Debe ingresar el número de RUC!"); }
      if (identificacion.trim().length !== 13) { validar("errorRuc", true); refRuc.current.focus(); return showError("¡Número de RUC tiene que contener 13 dígitos!"); }
      if (!verificarRuc(identificacion.trim())) { validar("errorRuc", true); return showError("¡Número de RUC inválido! Debe terminar en 001, 002 o 003."); }
    } else if (tipoDoc === "P") {
      if (identificacion === "") { validar("errorPasaporte", true); refPasaporte.current.focus(); return showError("¡Debe ingresar el número de pasaporte!"); }
      if (!verificarPasaporte(identificacion.trim())) { validar("errorPasaporte", true); return showError("¡Pasaporte inválido! Debe tener entre 5 y 13 caracteres alfanuméricos."); }
    }

    if (nombres === "") { validar("errorNombres", true); refNombres.current.focus(); return showError("¡Debe ingresar los nombres!"); }
    if (apellidos === "") { validar("errorApellidos", true); refApellidos.current.focus(); return showError("¡Debe ingresar sus apellidos!"); }
    if (clave === "") { validar("errorClave", true); refClave.current.focus(); return showError("¡Debe ingresar la clave!"); }
    if (clave2 !== clave) { validar("errorClave2", true); refClave2.current.focus(); return showError("¡Las claves no son iguales!"); }

    setLoading(true);
    let objUsuario = new Usuario(tipoDoc, identificacion, nombres.toUpperCase(), apellidos.toUpperCase(), clave, "A", hoy, null, "USER");

    try {
      // Verifica si ya esta registrado como usuario Anai
      let existenciaUsuarioAnai = await httpGet(urlRegistroExisteAnai + identificacion);
      if (existenciaUsuarioAnai.data.existe) {
        setLoading(false);
        return Swal.fire({ icon: "warning", title: "Usuario existente", text: "Ya existe un usuario ANAI con esa identificación.", confirmButtonColor: "#0d1b3e" });
      }

      // Verifica si ya esta registrado como usuario nuevo
      let existenciaUsuarioNew = await httpGet(urlRegistroExisteNew + identificacion);
      if (existenciaUsuarioNew.data.existe) {
        setLoading(false);
        return Swal.fire({ icon: "warning", title: "Usuario existente", text: "Ya existe un usuario nuevo con esa identificación.", confirmButtonColor: "#0d1b3e" });
      }

      // Registrar nuevo usuario
      let registro = await httpPost(urlRegistro, objUsuario);
      setLoading(false);

      if (registro.cod === 0) {
        await Swal.fire({ icon: "success", title: "¡Registro exitoso!", text: "Iniciando sesión automáticamente...", timer: 1800, showConfirmButton: false, confirmButtonColor: "#0d1b3e" });

        const data = { username: "A" + objUsuario.identificacion, password: objUsuario.clave };
        const options = { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(data) };

        fetch(`${urlAuth}auth/login`, options)
          .then((res) => res.json())
          .then(async (data) => {
            let _opciones = await httpGet(urlOpciones, data.token);
            let _grados = await httpGet(urlGetGrados, data.token);
            if (data.token) {
              dispatch({ type: "SET_OPCIONES", payload: _opciones.data });
              dispatch({ type: "SET_GRADOS", payload: _grados.data });
              dispatch({ type: "SET_TOKEN", payload: data });
              navigate("/inscripcion/list");
            }
          })
          .catch((err) => {
            console.log("Error de promesa:", err);
            Swal.fire({ icon: "error", title: "Error", text: "No se pudo iniciar sesión automáticamente.", confirmButtonColor: "#0d1b3e" });
          });
      } else {
        Swal.fire({ icon: "error", title: "Error", text: "¡Hubo un error, vuelva a intentarlo!", confirmButtonColor: "#0d1b3e" });
      }
    } catch (err) {
      setLoading(false);
      Swal.fire({ icon: "error", title: "Error de conexión", text: "No se pudo conectar al servidor.", confirmButtonColor: "#0d1b3e" });
    }
  };

  /* ----- LOADING ----- */
  if (loading) {
    return (
      <div className="login-bg" style={{ minHeight: "100vh" }}>
        <div className="modern-spinner fade-in">
          <div className="spinner-ring"></div>
          <span>Procesando registro...</span>
        </div>
      </div>
    );
  }

  /* ============================  JSX  ============================ */
  return (
    <div className="login-bg" style={{ minHeight: "100vh", paddingTop: 30, paddingBottom: 30 }}>
      <div className="login-card" style={{ maxWidth: 540 }}>
        <img src={logoanai} alt="ANAI Logo" className="login-logo" />
        <div className="login-title">Registro de Usuario</div>
        <div className="login-subtitle">
          Ingrese sus datos para registrarse y poder realizar inscripciones
        </div>

        {/* ─── Tipo de documento ─── */}
        <div style={{ width: "100%", marginBottom: 8 }}>
          <label style={{ fontWeight: 600, fontSize: "0.85rem", color: "#344767", marginBottom: 8, display: "block" }}>
            Tipo de Documento <span style={{ color: "#e53e3e" }}>*</span>
          </label>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            {[
              { val: "C", label: "\uD83E\uDEAA Cédula" },
              { val: "P", label: "\uD83D\uDEC2 Pasaporte" },
              { val: "R", label: "\uD83C\uDFE2 RUC" },
            ].map((opt) => (
              <label
                key={opt.val}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "8px 16px",
                  borderRadius: 8,
                  border: usuario.tipoDoc === opt.val ? "2px solid #0d1b3e" : "2px solid #e0e3e8",
                  background: usuario.tipoDoc === opt.val ? "rgba(13,27,62,0.06)" : "#fff",
                  cursor: "pointer",
                  transition: "all 0.2s",
                  fontWeight: usuario.tipoDoc === opt.val ? 700 : 400,
                  fontSize: "0.9rem",
                }}
              >
                <input
                  type="radio"
                  name="tipoDoc"
                  value={opt.val}
                  checked={usuario.tipoDoc === opt.val}
                  onChange={changeData}
                  style={{ display: "none" }}
                />
                {opt.label}
              </label>
            ))}
          </div>
        </div>

        {/* ─── Identificación ─── */}
        <div className="modern-input-group">
          <label>
            {currentId.label} <span style={{ color: "#e53e3e" }}>*</span>
          </label>
          <input
            ref={currentId.ref}
            type="text"
            name="identificacion"
            placeholder={currentId.placeholder}
            value={usuario.identificacion}
            onChange={changeData}
            onBlur={blurIdentificacion}
            maxLength={currentId.maxLen}
            style={error[currentId.errKey] ? { borderColor: "#e53e3e" } : {}}
          />
          {errorMsg.identificacion && <span style={errorMsgStyle}>{errorMsg.identificacion}</span>}
          {!errorMsg.identificacion && (
            <small style={{ color: "#7b809a", fontSize: "0.75rem", marginTop: 2 }}>
              Su número de identificación será su nombre de usuario.
            </small>
          )}
        </div>

        {/* ─── Nombres y Apellidos ─── */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, width: "100%" }}>
          <div className="modern-input-group">
            <label>Nombres <span style={{ color: "#e53e3e" }}>*</span></label>
            <input
              ref={refNombres}
              type="text"
              name="nombres"
              placeholder="Ingrese nombres"
              value={usuario.nombres}
              onChange={changeData}
              onBlur={blurNombres}
              style={error.errorNombres ? { borderColor: "#e53e3e" } : {}}
            />
            {errorMsg.nombres && <span style={errorMsgStyle}>{errorMsg.nombres}</span>}
          </div>
          <div className="modern-input-group">
            <label>Apellidos <span style={{ color: "#e53e3e" }}>*</span></label>
            <input
              ref={refApellidos}
              type="text"
              name="apellidos"
              placeholder="Ingrese apellidos"
              value={usuario.apellidos}
              onChange={changeData}
              onBlur={blurApellidos}
              style={error.errorApellidos ? { borderColor: "#e53e3e" } : {}}
            />
            {errorMsg.apellidos && <span style={errorMsgStyle}>{errorMsg.apellidos}</span>}
          </div>
        </div>

        {/* ─── Contraseñas ─── */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, width: "100%" }}>
          <div className="modern-input-group">
            <label>Contraseña <span style={{ color: "#e53e3e" }}>*</span></label>
            <div style={{ position: "relative" }}>
              <input
                ref={refClave}
                type={config.clave ? "text" : "password"}
                name="clave"
                placeholder="••••••••"
                value={usuario.clave}
                onChange={changeData}
                onBlur={blurClave}
                style={Object.assign(
                  { paddingRight: 42 },
                  error.errorClave ? { borderColor: "#e53e3e" } : {}
                )}
              />
              <button
                type="button"
                onClick={() => toggleShowPassword("clave")}
                style={{
                  position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)",
                  background: "none", border: "none", cursor: "pointer", fontSize: "1.1rem",
                  color: "#7b809a", padding: 4,
                }}
                title={config.clave ? "Ocultar" : "Mostrar"}
              >
                {config.clave ? "\uD83D\uDE48" : "\uD83D\uDC41\uFE0F"}
              </button>
            </div>
            {errorMsg.clave && <span style={errorMsgStyle}>{errorMsg.clave}</span>}
          </div>
          <div className="modern-input-group">
            <label>Confirmar <span style={{ color: "#e53e3e" }}>*</span></label>
            <div style={{ position: "relative" }}>
              <input
                ref={refClave2}
                type={config.clave2 ? "text" : "password"}
                name="clave2"
                placeholder="••••••••"
                value={usuario.clave2}
                onChange={changeData}
                onBlur={blurClave2}
                style={Object.assign(
                  { paddingRight: 42 },
                  error.errorClave2 ? { borderColor: "#e53e3e" } : {}
                )}
              />
              <button
                type="button"
                onClick={() => toggleShowPassword("clave2")}
                style={{
                  position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)",
                  background: "none", border: "none", cursor: "pointer", fontSize: "1.1rem",
                  color: "#7b809a", padding: 4,
                }}
                title={config.clave2 ? "Ocultar" : "Mostrar"}
              >
                {config.clave2 ? "\uD83D\uDE48" : "\uD83D\uDC41\uFE0F"}
              </button>
            </div>
            {errorMsg.clave2 && <span style={errorMsgStyle}>{errorMsg.clave2}</span>}
          </div>
        </div>

        {/* ─── Botones ─── */}
        <div style={{ display: "flex", gap: 12, width: "100%", marginTop: 8 }}>
          <button
            className="btn-modern btn-outline"
            style={{ flex: 1 }}
            onClick={() => navigate("/")}
          >
            ← Atrás
          </button>
          <button
            className="btn-modern btn-save"
            style={{ flex: 2 }}
            onClick={save}
          >
            📝 Registrarse
          </button>
        </div>

        <div className="modern-copyright">
          {"© " + new Date().getFullYear() + " "}
          <a href="https://anai.edu.ec/" target="_blank" rel="noopener noreferrer">
            Academia Naval Almirante Illingworth
          </a>
          <br />
          <span style={{ fontSize: '0.75rem', color: '#999', marginTop: 4, display: 'inline-block' }}>Diseñado y Desarrollado por Departamento de Sistemas ANAI</span>
        </div>
      </div>
    </div>
  );
}
